<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: this.normalizeSize(55) }"
    >
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />
            <!-- ⚠️ Message d'erreur serveur -->
            <div
                v-if="error"
                class="ppv2-mb-4 ppv2-p-4 ppv2-rounded-lg ppv2-bg-[#ed4b9e] ppv2-bg-opacity-10 ppv2-border ppv2-border-[#ed4b9e]"
            >
                <span class="error-text">{{ error }}</span>
            </div>

            <!-- 🔥 BADGE DE DÉTECTION DU TYPE -->
            <div v-if="detectedType && showDetectedType" class="detection-badge-container ppv2-mb-4">
                <div
                    class="detection-badge ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-1 ppv2-rounded-lg ppv2-border ppv2-mb-7"
                    :class="[
                        hasDetectionAmbiguity
                            ? 'ppv2-bg-orange-50 ppv2-border-orange-400'
                            : 'ppv2-bg-[#31d0aa] ppv2-bg-opacity-10 ppv2-border-[#31d0aa]',
                        'dark:ppv2-bg-opacity-10',
                    ]"
                >
                    <span class="ppv2-text-xl" :class="hasDetectionAmbiguity ? 'ppv2-text-orange-600' : 'ppv2-text-[#31d0aa]'">
                        {{ hasDetectionAmbiguity ? '⚠️' : '✓' }}
                    </span>
                    <div class="ppv2-flex-1">
                        <div
                            class="poppinsMedium ppv2-text-sm"
                            :class="hasDetectionAmbiguity ? 'ppv2-text-orange-800' : 'ppv2-text-[#31d0aa]'"
                        >
                            {{ detectionMessage }}
                        </div>
                        <div v-if="hasDetectionAmbiguity" class="ppv2-text-xs ppv2-text-orange-600 ppv2-mt-1">
                            {{ $t('experience.step3.detection.ambiguityHint') }}
                        </div>
                    </div>
                    <div class="ppv2-text-xl poppinsBold ppv2-text-black ppv2-text-opacity-70">
                        {{ Math.round(detectionInfo.score * 100) }}%
                    </div>
                </div>
            </div>

            <!-- Reservation Mode -->
            <SelectableList
                :label="$t('experience.step4.reservationMode.Question')"
                v-model="selectedOptionReservationMode"
                :options="options_reservation_mode"
                :disabled="isPublishedPrestation"
                :tooltipMsg="reservationModeTooltip"
                class="ppv2-mb-[20px]"
                @input="clearError('reservationMode')"
            />

            <div v-if="errors.reservationMode" class="ppv2-text-[#ed4b9e] ppv2-text-xs ppv2-mt-[-15px] ppv2-mb-[20px]">
                {{ errors.reservationMode }}
            </div>

            <div
                v-if="errors.oneScheduleInstallation"
                class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-text-[#b45309] ppv2-text-xs ppv2-mt-[15px] ppv2-mb-[20px] ppv2-bg-[#fef3c7] ppv2-p-2 ppv2-rounded ppv2-border ppv2-border-[#f59e0b]"
            >
                <span class="ppv2-flex-1">{{ errors.oneScheduleInstallation }}</span>
                <button
                    type="button"
                    @click.stop="clearOneScheduleError"
                    class="ppv2-ml-2 ppv2-text-[#b45309] hover:poppinsBold ppv2-cursor-pointer ppv2-px-1"
                >
                    ✕
                </button>
            </div>

            <!-- Installations -->
            <ConfigListInsalationEquipement
                :title="$t('experience.step3.installation.title')"
                :items="visibleInstallations"
                type="installations"
                @add="openAddPopup"
                @edit="editItem"
                @delete="deleteItem"
                @toggle="handleToggle"
                :activeField="activeField"
            />

            <p v-if="errors.installations" class="ppv2-text-[#ed4b9e] ppv2-text-sm ppv2-mt-1">
                {{ errors.installations }}
            </p>

            <!-- Équipements -->
            <ConfigListInsalationEquipement
                v-show="showEquipements"
                :title="$t('experience.step3.equipment.title')"
                :items="visibleEuipements"
                type="equipements"
                @add="openAddPopup"
                @edit="editItem"
                @delete="deleteItem"
                @toggle="handleToggle"
                :activeField="activeField"
                :singleSelection="reservationMode === 'one_schedule_for_everyone' || reservationMode === 'free_access_all_day'"
            />

            <DashedCard
                v-show="showDashedCapacityOptions"
                :title="$t('experience.step3.dashedCapacityOptions.title')"
                :title-size="15"
                custom-class=""
                :custom-class-title="errors.advancedOptions ? '!ppv2-text-[#ed4b9e]' : ''"
                :marginTop="37"
                :marginBottom="37"
                min-height="115px"
                :custom-style="{
                    padding: normalizeSize(24, true),
                    ...(errors.advancedOptions ? { borderColor: '#ed4b9e' } : {}),
                }"
            >
                <template #content>
                    <div
                        class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-start ppv2-justify-start"
                        :style="{ gap: normalizeSize(16, true) }"
                    >
                        <BaseSwitch
                            v-model="multi_space_group_mix"
                            :label="$t('experience.step3.dashedCapacityOptions.option_1')"
                            :disabled="isMultiSpaceGroupMixDisabled"
                            @input="handleMultiSpaceGroupMixChange"
                        />

                        <!-- FLEX ROW FOR SWITCH + INPUT -->
                        <div class="ppv2-flex ppv2-items-center" :style="{ gap: normalizeSize(12, true) }">
                            <BaseSwitch
                                v-model="prohibit_mixing_based_on_the_number_of_people"
                                :label="$t('experience.step3.dashedCapacityOptions.option_2')"
                                :disabled="isMixingLimitDisabled"
                            />
                            <CustomInput
                                v-if="prohibit_mixing_based_on_the_number_of_people"
                                v-model="the_number_of_people"
                                placeholder="0"
                                type="number"
                                :state="getState('the_number_of_people')"
                                :errorText="errors.the_number_of_people"
                                @input="clearError('the_number_of_people')"
                                style="width: 80px"
                                :disabled="!isChoiceEditable('prohibited_mixing_min_people_value')"
                                :floatingLabel="true"
                                :customSizeLabel="13"
                                classInputAdd="!ppv2-rounded-lg !ppv2-h-[30px] !ppv2-min-h-[30px]"
                            />
                        </div>
                        <!-- END FLEX ROW -->

                        <BaseSwitch
                            v-model="same_space_group_mix"
                            :label="$t('experience.step3.dashedCapacityOptions.option_same_space')"
                            :disabled="isSameSpaceGroupMixDisabled"
                            @input="handleSameSpaceGroupMixChange"
                        />

                        <!-- Choix de l'espace par le client -->
                        <BaseSwitch
                            v-model="client_space_choice"
                            :label="$t('experience.step3.dashedCapacityOptions.option_3')"
                            :disabled="isClientSpaceChoiceDisabled"
                            @input="handleClientSpaceChoiceChange"
                        />

                        <!-- Durée dynamique : cocher DÉCLARE un bowling.
                             Contrairement aux autres interrupteurs, celui-ci ne décrit pas une
                             contrainte de capacité : il choisit le circuit, et applique son
                             préréglage pour que la détection tombe forcément juste. -->
                        <BaseSwitch
                            v-model="dynamic_duration_enabled"
                            :label="$t('experience.step3.dashedCapacityOptions.option_dynamic_duration')"
                            :disabled="isPublishedPrestation"
                            @input="handleDynamicDurationChange"
                        />
                    </div>
                    <p v-if="errors.advancedOptions" class="ppv2-text-[#ed4b9e] ppv2-text-sm ppv2-mt-2">
                        {{ errors.advancedOptions }}
                    </p>
                </template>
            </DashedCard>

            <!-- Popup -->
            <CustomDialog v-model="popupVisible" :defaultTitle="popupTitle" @confirm="saveItem" @cancel="closePopup">
                <template #content>
                    <div>
                        <CustomInput
                            :label="
                                currentType === 'installations'
                                    ? $t('experience.step3.installation.name')
                                    : $t('experience.step3.equipment.name')
                            "
                            :required="true"
                            v-model="form.name"
                            :placeholder="
                                currentType === 'installations'
                                    ? $t('experience.step3.installation.placeholder.name')
                                    : $t('experience.step3.equipment.placeholder.name')
                            "
                            :state="getState('name')"
                            :errorText="errors.name"
                            @input="clearError('name')"
                            :customSizeLabel="13"
                        />
                    </div>

                    <div>
                        <CustomInput
                            :label="
                                currentType === 'installations'
                                    ? $t('experience.step3.installation.capacity')
                                    : $t('experience.step3.equipment.capacity')
                            "
                            :required="true"
                            v-model="form.capacity"
                            placeholder="Ex: 20"
                            type="number"
                            :state="getState('capacity')"
                            :errorText="errors.capacity"
                            @input="clearError('capacity')"
                            :customSizeLabel="13"
                        />
                    </div>

                    <div class="pb-5" v-show="currentType === 'installations'">
                        <BaseSwitch v-model="form.mix" :label="$t('experience.step3.installation.mixingGroup')" />
                    </div>
                    <div class="pb-5" v-show="currentType === 'installations'">
                        <BaseSwitch
                            v-model="form.karafun"
                            :label="$t('experience.step3.installation.karafun')"
                            :disabled="!isKarafunConfigured"
                        />
                        <InfoBanner v-if="!isKarafunConfigured" variant="warning" class="ppv2-mt-3" :customFontSize="12">
                            {{ $t('experience.step3.installation.karafunNotConfigured') }}
                            <a href="/back-office/v3/integration" target="_blank" class="poppinsSemiBold ppv2-underline">{{
                                $t('experience.step3.installation.karafunGoToIntegration')
                            }}</a>
                        </InfoBanner>
                    </div>
                    <div v-show="currentType === 'installations'">
                        <KarafunDevice
                            v-if="form.karafun"
                            :selectedDevices.sync="form.devices"
                            :devices="devices"
                            @sync="fetchKarafunDevices"
                        />
                    </div>
                </template>
            </CustomDialog>

            <!-- Delete Confirmation Popup -->
            <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
                <template #content>
                    <p
                        class="ppv2-text-center ppv2-text-black poppinsRegular ppv2-whitespace-pre-line"
                        :style="{ fontSize: normalizeFontSize(15) }"
                    >
                        {{ deletePopupDescription }}
                    </p>
                </template>
                <template #actions>
                    <BaseButton
                        id="button"
                        type="button"
                        variant="outline"
                        @click="showDeletePopup = false"
                        customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                        :customHeight="45"
                    >
                        {{ $t('equipements.deletePopup.cancel') }}
                    </BaseButton>
                    <BaseButton
                        type="submit"
                        variant="danger"
                        @click="confirmDelete"
                        customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                        :customHeight="45"
                    >
                        {{ $t('equipements.deletePopup.confirm') }}
                    </BaseButton>
                </template>
            </CustomDialog>

            <!-- Boutons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': this.complited }">
                <BackButton v-show="!this.complited" type="button" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="submit" variant="primary" @click="submitForm" :loading="loading">
                    {{ !this.complited ? $t('experience.next') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>
        <!-- Aperçu -->
        <PreviewExperience :experience="options_reservation_mode" :activeField="activeField" :step="step" />
    </div>
</template>

<script>
import { Pencil, Trash2, Users, RefreshCw, Plus } from 'lucide-vue'
import HeaderStep from './HeaderStep.vue'
import ConfigListInsalationEquipement from './ConfigListInsalationEquipement.vue'
import BackButton from '../../../Shared/BackButton.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseInput from '../../../Shared/BaseInput.vue'
import KarafunDevice from './KarafunDevice.vue'
import PreviewExperience from './PreviewExperience.vue'
import CustomButtonSave from '../../../Shared/CustomButtonSave.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseLabel from '../../../Shared/BaseLabel.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import ExperienceMixin from '../mixins/ExperienceMixin'
import SelectableList from '../../../Shared/SelectableList.vue'
import InfoBanner from '../../../Shared/InfoBanner.vue'
import { circuitDetectionService, CIRCUIT_TYPES, CONFIDENCE_LEVELS } from '../../../../services/CircuitDetectionService'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    components: {
        BaseLabel,
        CustomInput,
        CustomButtonSave,
        BaseButton,
        BackButton,
        HeaderStep,
        Pencil,
        Trash2,
        Users,
        RefreshCw,
        ConfigListInsalationEquipement,
        CustomDialog,
        SelectableList,
        BaseSwitch,
        BaseInput,
        KarafunDevice,
        Plus,
        PreviewExperience,
        DashedCard,
        InfoBanner,
    },
    mixins: [ExperienceMixin],
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        prestation: { type: Object, required: false },
        spaces_id: { type: Array, required: false },
        selected_experience: { type: Object, required: false },
        id: { type: [Number, String], default: null },
        category: {
            type: Object,
            required: false,
        },
        sub_category: {
            type: Object,
            required: false,
        },
        complited: {
            type: Boolean,
            required: false,
        },
    },
    data() {
        return {
            showEquipements: false,
            equipements: [],
            popupVisible: false,
            showDeletePopup: false,
            deletePopupTitle: this.$t('equipements.deletePopup.title'),
            deletePopupDescription: '',
            itemToDeleteIndex: null,
            itemToDeleteType: '',
            currentType: '',
            editingIndex: null,
            form: { name: '', capacity: '', mix: false, karafun: false, devices: [], selected: false, deleted: false, id: '' },
            errors: {
                oneScheduleInstallation: null,
            },
            devices: [],
            loadingDevices: false,
            isKarafunConfigured: false,
            selectedOptionReservationMode: null,
            options_reservation_mode: [
                {
                    label: this.$t('experience.step4.reservationMode.ResponseTitle1'),
                    value: 'multiple_time_slots',
                    subtitle: this.$t('experience.step4.reservationMode.Description1'),
                },
                {
                    label: this.$t('experience.step4.reservationMode.ResponseTitle2'),
                    value: 'one_schedule_for_everyone',
                    subtitle: this.$t('experience.step4.reservationMode.Description2'),
                },
                {
                    label: this.$t('experience.step4.reservationMode.ResponseTitle3'),
                    value: 'free_access_all_day',
                    subtitle: this.$t('experience.step4.reservationMode.Description3'),
                },
            ],
            activeField: null,
            localPrestation: null,
            localSpaces: [],
            loading: false,
            error: null,
            initialDataString: null,
            isInitialLoad: true, // ✅ FIX: Track initial load to prevent auto-deactivation
            multi_space_group_mix: false,
            warningTimer: null,
            same_space_group_mix: false,
            prohibit_mixing_based_on_the_number_of_people: false,
            the_number_of_people: 1,
            client_space_choice: false,
            dynamic_duration_enabled: false,
            min_places_to_play: 1,
            pricing_mode: null,
            requiredSpaces: 1,
            lastChangedField: null,
            // Circuit Detection Service constants
            CIRCUIT_TYPES,
            CONFIDENCE_LEVELS,
        }
    },
    computed: {
        showDashedCapacityOptions() {
            return (
                this.countChecked() >= this.requiredSpaces &&
                !(this.matchingCircuits.length === 1 && ['simpleTicket', 'event'].includes(this.matchingCircuits[0]))
            )
        },
        installations() {
            return this.$store.getters['experience/installations']
        },
        reservationMode() {
            return this.$store.getters['experience/selectedReservationMode']
        },
        currentCircuit() {
            return this.$store.getters['experience/currentCircuit']
        },
        matchingCircuits() {
            return this.$store.getters['experience/getMatchingCircuits']
        },
        userSelections() {
            return this.$store.getters['experience/userSelections']
        },

        // 🔥 DÉTECTION AVEC CircuitDetectionService
        detectionInfo() {
            // Use CircuitDetectionService for real-time detection
            const userSelections = {
                reservation_mode: this.$store.getters['experience/reservationMode'],
                selectedOptionTarifMode: this.$store.getters['experience/selectedOptionTarifMode'],
                isActiveProhibitMixingMinPeople: this.$store.getters['experience/isActiveProhibitMixingMinPeople'],
                prohibitMixingMinPeople: this.$store.getters['experience/prohibitMixingMinPeople'],
                dynamic_duration_enabled: this.$store.getters['experience/userSelections']?.dynamic_duration_enabled ?? null,
            }

            const detection = circuitDetectionService.getBestDetection(userSelections)

            if (!detection.success) {
                return {
                    type: null,
                    score: 0,
                    confidence: CONFIDENCE_LEVELS.LOW,
                    isAmbiguous: false,
                }
            }

            return {
                type: detection.type,
                score: detection.score / 100, // Normalize to 0-1 for percentage display
                confidence: detection.confidence,
                isAmbiguous: detection.isAmbiguous,
                allScores: detection.allScores,
            }
        },
        detectedType() {
            const info = this.detectionInfo
            // Afficher le type seulement si confiance >= high
            return info.confidence === CONFIDENCE_LEVELS.EXACT || info.confidence === CONFIDENCE_LEVELS.HIGH ? info.type : null
        },
        // ✅ Control visibility of detected type block from store
        showDetectedType() {
            return this.$store.getters['experience/showDetectedType']
        },
        detectionScores() {
            return this.detectionInfo.allScores || {}
        },
        detectionMessage() {
            const info = this.detectionInfo
            if (!info.type) {
                return this.$t('experience.step3.detection.waitingConfig')
            }

            const typeName = circuitDetectionService.getTypeName(info.type)
            const typeDesc = circuitDetectionService.getTypeDescription(info.type)

            if (info.isAmbiguous) {
                return this.$t('experience.step3.detection.detectedTypeAmbiguous', { typeName })
            }

            return this.$t('experience.step3.detection.detectedType', { typeName })
        },
        hasDetectionAmbiguity() {
            return this.detectionInfo.isAmbiguous || false
        },
        detectionBadgeColor() {
            const conf = this.detectionInfo.confidence
            switch (conf) {
                case CONFIDENCE_LEVELS.EXACT:
                    return 'green'
                case CONFIDENCE_LEVELS.HIGH:
                    return 'blue'
                case CONFIDENCE_LEVELS.MEDIUM:
                    return 'orange'
                case CONFIDENCE_LEVELS.LOW:
                    return 'grey'
                default:
                    return 'grey'
            }
        },
        hasId() {
            return this.id !== null && this.id !== undefined
        },
        popupTitle() {
            const key = this.editingIndex !== null ? 'experience.step3.popup.edit' : 'experience.step3.popup.add'
            return this.$t(key, { item: this.currentTypeLabel })
        },
        currentTypeLabel() {
            return this.$t(
                this.currentType === 'installations' ? 'experience.step3.popup.installation' : 'experience.step3.popup.equipment'
            )
        },
        localSelectExperience() {
            return this.selected_experience || {}
        },
        visibleInstallations() {
            return this.$store.getters['experience/visibleInstallations']
        },
        visibleEuipements() {
            return this.equipements.filter((i) => !i.deleted)
        },
        hasReservationModeFromApi() {
            const reservationMode = this.getUserChoiceValue('reservation_mode', this.localPrestation)
            return !!reservationMode && reservationMode !== ''
        },

        // Check if prestation is published (not a draft)
        isPublishedPrestation() {
            return this.localPrestation && !this.localPrestation.draft
        },

        // Dynamic tooltip for reservation mode
        reservationModeTooltip() {
            if (this.isPublishedPrestation) {
                return this.$t('experience.step4.reservationMode.tooltipPublished')
            }
            return this.$t('experience.step4.reservationMode.Tooltip')
        },

        // ✅ FIX: Smart circuit-based field disabling
        // - During initial load: Never disable (preserve saved values)
        // - After user interaction: Re-enable circuit-based disabling
        isFieldDisabledByCircuits() {
            return (fieldName) => {
                // During initial load, never disable fields (preserve DB values)
                if (this.isInitialLoad) {
                    return false
                }

                // After initial load, apply normal circuit-based disabling
                if (!this.matchingCircuits || this.matchingCircuits.length === 0) {
                    return false // No circuits = no restriction
                }

                // Check if ALL matching circuits have value=0 for this field
                const allHaveZero = this.matchingCircuits.every((circuitType) => {
                    const config = this.$store.getters['experience/getPrestationTypeConfig'](circuitType)
                    return config && config[fieldName] === 0
                })

                return allHaveZero
            }
        },

        // Individual field disabled states
        // DETECTION_KEY fields (user inputs) should NEVER be disabled by circuit matching
        // to avoid self-locking traps (toggle off → store has 0 → matching narrows → field disabled forever)
        // Only mutual-exclusivity rules apply.
        isMultiSpaceGroupMixDisabled() {
            return this.isPublishedPrestation
        },
        isSameSpaceGroupMixDisabled() {
            return false // User always free to toggle
        },
        isClientSpaceChoiceDisabled() {
            return this.multi_space_group_mix || this.isPublishedPrestation
        },
        isMinPlayersToPlayDisabled() {
            return false // No UI element - this field doesn't have a visible switch
        },
        isMixingLimitDisabled() {
            return this.multi_space_group_mix || this.isPublishedPrestation
        },
        selectedInstallations() {
            const installations = this.$store.getters['experience/installations']
            // Filter for truthy 'selected' property
            return installations ? installations.filter((item) => item.selected) : []
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        getState(field) {
            if (this.errors[field]) return 'error'
            if (this.form[field]) return 'valid'
            return 'default'
        },
        clearError(field) {
            if (this.errors[field]) {
                this.$set(this.errors, field, null)
            }
        },
        clearOneScheduleError() {
            if (this.warningTimer) {
                clearTimeout(this.warningTimer)
                this.warningTimer = null
            }
            this.$set(this.errors, 'oneScheduleInstallation', null)
        },
        openAddPopup(type) {
            this.currentType = type
            this.popupVisible = true
            this.editingIndex = null
            this.form.selected = true
            this.form.karafun = this.isKarafunConfigured && this.form.karafun
        },
        editItem(index, type) {
            this.currentType = type
            this.editingIndex = index
            const item = type === 'installations' ? this.visibleInstallations[index] : this.equipements[index]
            this.form = { ...item }
            if (!this.isKarafunConfigured) {
                this.form.karafun = false
            }
            this.popupVisible = true
        },
        deleteItem(index, type) {
            this.itemToDeleteIndex = index
            this.itemToDeleteType = type

            const item = type === 'installations' ? this.visibleInstallations[index] : this.equipements[index]

            this.deletePopupDescription = this.$t('equipements.deletePopup.message')
            if (item && item.prestations_count > 0) {
                this.deletePopupDescription +=
                    '\n' + this.$t('equipements.deletePopup.deleteWarning', { count: item.prestations_count })
            }

            this.showDeletePopup = true
        },
        confirmDelete() {
            const index = this.itemToDeleteIndex
            const type = this.itemToDeleteType

            if (type === 'installations') {
                const installations = [...this.$store.getters['experience/installations']]
                const itemToDelete = this.visibleInstallations[index]
                const realIndex = installations.findIndex((i) => i === itemToDelete)
                if (realIndex !== -1) {
                    installations[realIndex] = { ...installations[realIndex], deleted: true }
                    this.$store.dispatch('experience/setInstallations', installations)
                }
            } else {
                this.$set(this.equipements, index, {
                    ...this.equipements[index],
                    deleted: true,
                })
            }

            this.showDeletePopup = false
        },
        saveItem() {
            this.errors = {}
            if (!this.form.name.trim()) this.errors.name = this.$t('experience.step3.name.required')
            if (!this.form.capacity) {
                this.errors.capacity = this.$t('experience.step3.capacity.required')
            } else if (isNaN(this.form.capacity) || this.form.capacity <= 0) {
                this.errors.capacity = this.$t('experience.step3.capacity.error')
            }
            if (Object.keys(this.errors).length > 0) return

            const newItem = { ...this.form }

            if (this.currentType === 'installations') {
                const installations = [...this.$store.getters['experience/installations']]

                if (
                    (this.reservationMode === 'one_schedule_for_everyone' || this.reservationMode === 'free_access_all_day') &&
                    newItem.selected
                ) {
                    installations.forEach((i) => (i.selected = false))
                }

                if (this.editingIndex !== null) {
                    const itemToEdit = this.visibleInstallations[this.editingIndex]
                    const realIndex = installations.findIndex((i) => i === itemToEdit)
                    if (realIndex !== -1) installations.splice(realIndex, 1, newItem)
                } else {
                    installations.push(newItem)
                }
                this.$store.dispatch('experience/setInstallations', installations)
            } else {
                if (this.editingIndex !== null) this.equipements.splice(this.editingIndex, 1, newItem)
                else this.equipements.push(newItem)
            }

            this.closePopup()
        },
        closePopup() {
            this.popupVisible = false
            this.resetForm()
        },
        resetForm() {
            this.form = { name: '', capacity: '', mix: false, karafun: false, devices: [], selected: false, id: '' }
        },
        async fetchKarafunDevices() {
            this.loadingDevices = true
            try {
                const response = await axios.get('/api/v1/karafun/list')
                this.devices = response.data.devices || response.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loadingDevices = false
            }
        },
        async checkKarafunIntegration() {
            try {
                const response = await axios.get('/api/v1/karafun/check')
                this.isKarafunConfigured = response.data.configured === true
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.isKarafunConfigured = false
            }
        },
        async saveAndExit() {
            if (this.countChecked() === 0) {
                this.$set(this.errors, 'installations', this.$t('experience.step3.installation.at_least_one'))
                return
            }
            const realErrors = Object.keys(this.errors).filter((k) => this.errors[k])
            if (realErrors.length === 0) {
                this.$emit('save-and-exit')
            }
        },
        // required bloc installations
        countChecked() {
            return this.visibleInstallations.filter((i) => i.selected).length
        },
        async submitForm() {
            // ✅ FIX: Reset ALL errors (clears stale backend keys from previous 422)
            this.errors = {}
            this.error = null

            if (!this.selectedOptionReservationMode) {
                this.$set(this.errors, 'reservationMode', this.$t('experience.errors.required'))
            }

            if (this.countChecked() === 0) {
                this.$set(this.errors, 'installations', this.$t('experience.step3.installation.at_least_one'))
            }

            const realErrors = Object.keys(this.errors).filter((k) => this.errors[k])
            if (realErrors.length === 0) {
                // ✅ FIX: Attendre le résultat et seulement naviguer si succès
                const success = await this.saveExperience(false)
                if (success) {
                    this.$toast.success(this.$t('experience.saved'))
                    if (!this.complited) {
                        this.$emit('next-step')
                    } else {
                        // L'erreur est déjà affichée par saveExperience()
                        console.error('❌ Impossible de passer au step suivant - erreur de sauvegarde')
                    }
                }
            }
        },
        async saveExperience(redirect = false) {
            this.loading = true
            this.error = null

            // Primary detected type uses exact matching (not scoring) for backend accuracy
            const primaryDetectedType = this.$store.getters['experience/getPrimaryDetectedType']
            const userSelections = this.$store.getters['experience/userSelections']

            const formData = {
                step: this.step.id,
                installations: this.selectedInstallations,
                equipements: this.equipements,
                selectExperience: this.localSelectExperience,
                active: this.prestation?.active || false,
                mixGroupeMultiEspace: this.multi_space_group_mix,
                prohibitMixingMinPeople: this.the_number_of_people,
                isActiveProhibitMixingMinPeople: this.prohibit_mixing_based_on_the_number_of_people,
                clientSpaceChoice: this.client_space_choice,
                dynamicDurationEnabled: this.dynamic_duration_enabled,
                // Le mode tarifaire n'est PLUS persisté depuis l'étape 3 : l'empreinte bowling ne
                // dépend plus du mode (par personne OU par groupe), la détection tombe juste sans
                // lui. Le choix réel — modifiable — est celui de l'étape 5 (Tarification).
                sameSpaceGroupMix: this.same_space_group_mix,
                minPlacesToPlay: this.min_places_to_play,
                selectedOptionReservationMode: this.selectedOptionReservationMode,

                detected_type: primaryDetectedType,
                detection_score: primaryDetectedType ? 1.0 : null,
                detection_confidence: primaryDetectedType ? 'exact' : null,
                user_selections: userSelections,
            }
            if (this.localPrestation?.id) formData.id = this.localPrestation.id

            try {
                const object = await axios.post('/api/v3/experience/save_exit_step_two', formData)
                this.$emit('update-prestation', object.data.data)

                // 🐛 FIX duplication installation après édition :
                // Une installation fraîchement créée gardait id:'' dans le store (l'id réel du
                // Space n'était jamais renvoyé au front), et le garde "navigating back" du watcher
                // spaces_id empêchait tout rechargement. Au save suivant (édition), le backend
                // recevait à nouveau un id vide et allouait un NOUVEAU Space (max(id)+1) → doublon,
                // l'original se retrouvant délié (0 expérience) et le doublon lié (1 expérience).
                // On recharge donc les installations depuis la BDD pour récupérer les id réels.
                // Pas de perte de données : getSpaces renvoie tous les espaces de l'établissement,
                // `selected` étant repositionné à partir des espaces réellement liés (spaces_id).
                const persistedSpaceIds = object.data?.spaces_id
                if (!redirect && Array.isArray(persistedSpaceIds) && persistedSpaceIds.length > 0) {
                    this.localSpaces = persistedSpaceIds
                    await this.fetchSpaces(object.data?.data?.establishment_id)
                    this.$nextTick(() => {
                        this.initialDataString = this.getFormDataString()
                    })
                }

                if (redirect) {
                    const baseUrl = `${window.location.protocol}//${window.location.host}`
                    window.location.href = `${baseUrl}/back-office/v3/experience`
                } else {
                    console.log('✅ Données enregistrées avec succès (sans redirection).')
                }
                return true // ✅ Succès - allow navigation to next step
            } catch (err) {
                console.error('❌ Step3 - Error saving:', err.response || err)

                // ✅ FIX: Handle 422 validation errors specifically
                if (err.response?.status === 422 && err.response?.data?.errors) {
                    // Populate errors object with validation errors
                    const validationErrors = err.response.data.errors
                    Object.keys(validationErrors).forEach((key) => {
                        this.$set(this.errors, key, validationErrors[key][0]) // Take first error message
                    })
                    this.error = err.response.data.message || this.$t('common.formValidationError')
                } else {
                    // Generic error message for 500 or other errors
                    this.error = err.response?.data?.message || this.$t('experience.errors.save')
                    this.$set(this.errors, 'server', this.error)
                }

                return false // ✅ Échec - block navigation on error
            } finally {
                this.loading = false
            }
        },
        async fetchSpaces(establishmentIdOverride = null) {
            if (!this.localSpaces) {
                console.warn('⚠️ Aucun espace sélectionné, fetchSpaces() ignoré.')
                return
            }

            this.loading = true
            try {
                const establishmentId = establishmentIdOverride ?? this.prestation?.establishment_id ?? 1
                const { data } = await axios.get(`/back-office/prestation/spaces/${establishmentId}`, {
                    params: {
                        type: this.prestation?.type,
                        spaces_selected: this.localSpaces.join(','),
                    },
                })
                // Clear store before inserting to avoid duplicates
                this.$store.dispatch('experience/setInstallations', [])
                const rawList = Array.isArray(data) ? data : Object.values(data || {})

                const installations = rawList.map((space) => ({
                    id: space.id || null,
                    name: space.name || '',
                    capacity: space.capacity || 0,
                    mix: !!space.authorize_mixing,
                    karafun: !!(space.karafun_devices?.length > 0),
                    devices: space.karafun_devices?.map((device) => device.id) || [],
                    selected: !!space.selected,
                    deleted: false,
                    prestations_count: space.prestations_count ?? 0,
                }))

                this.$store.dispatch('experience/setInstallations', installations)
            } catch (err) {
                console.error('❌ Erreur lors du chargement des espaces :', err.response || err)
                this.error = err.response?.data?.message || this.$t('experience.step3.installation.error_fetch')
            } finally {
                this.loading = false
            }
        },
        async fetchMateriels() {
            try {
                const id_prestation = this.prestation?.id || null
                const materiels = await axios.get(`/api/v3/experience/fetch/materiels/${id_prestation}`)
                if (materiels && Array.isArray(materiels.data.materials)) {
                    this.equipements = materiels.data.materials.map((m) => ({
                        id: m.id,
                        name: m.name,
                        capacity: m.quantity,
                        mix: false,
                        karafun: false,
                        devices: [],
                        selected: m.selected ?? false,
                        deleted: false,
                        prestations_count: m.prestations_count ?? 0,
                    }))
                }
            } catch (err) {
                console.error('❌ Erreur lors du chargement des matériels :', err.response || err)
                this.error = err.response?.data?.message || this.$t('experience.step3.equipment.error_fetch')
            }
        },
        handleToggle({ index, item, type }) {
            this.activeField = 'installations'
            console.log('✅ Selected items:', item)
            console.log('✅ reservation_mode:', this.reservationMode)

            // Logic: If mode is 'one_schedule_for_everyone' and type is 'installations'
            // Ensure only one item is selected at a time.
            if (
                type === 'installations' &&
                (this.reservationMode === 'one_schedule_for_everyone' || this.reservationMode === 'free_access_all_day')
            ) {
                // ✅ FIX: Removed redundant line (already set on line 762)
                if (item.selected) {
                    const installations = [...this.$store.getters['experience/installations']]
                    // Uncheck all others
                    installations.forEach((inst) => {
                        if (inst !== item && !inst.deleted) {
                            inst.selected = false
                        }
                    })
                    this.$store.dispatch('experience/setInstallations', installations)
                }
            }
        },

        exit() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },
        getFormDataString() {
            return JSON.stringify({
                installations: this.$store.getters['experience/installations'],
                equipements: this.equipements,
                multi_space_group_mix: this.multi_space_group_mix,
                prohibit_mixing_based_on_the_number_of_people: this.prohibit_mixing_based_on_the_number_of_people,
                the_number_of_people: this.the_number_of_people,
                same_space_group_mix: this.same_space_group_mix,
                client_space_choice: this.client_space_choice,
                selectedOptionReservationMode: this.selectedOptionReservationMode,
            })
        },
        hasChanges() {
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },
        normalizeSize,
        isFieldDisabledByCircuit(fieldName) {
            // If no circuit is detected, all fields are editable
            const matchingCircuits = this.matchingCircuits
            if (!matchingCircuits || matchingCircuits.length === 0) return false

            // Get the current value of the field
            const currentValue = this[this.getLocalFieldName(fieldName)]

            // If the field is currently active (true/1), allow deselection
            if (currentValue === true || currentValue === 1) {
                return false // Allow deselection
            }

            // Check if ALL matching circuits have value 0 for this field
            const allCircuitsDisableField = matchingCircuits.every((circuitType) => {
                const config = this.$store.getters['experience/getPrestationTypeConfig'](circuitType)
                return config && config[fieldName] === 0
            })

            // If all matching circuits want this field disabled, disable it
            if (allCircuitsDisableField) {
                return true // Disable field
            }

            // Check if ALL matching circuits have value 1 for this field
            const allCircuitsEnableField = matchingCircuits.every((circuitType) => {
                const config = this.$store.getters['experience/getPrestationTypeConfig'](circuitType)
                return config && config[fieldName] === 1
            })

            // If all matching circuits want this field enabled, allow it
            if (allCircuitsEnableField) {
                return false // Allow activation
            }

            // If circuits disagree (some want 0, some want 1), don't disable
            // Let user choose freely
            return false
        },
        getLocalFieldName(storeFieldName) {
            // Map store field names to local component field names
            const mapping = {
                multi_space_group_mix: 'multi_space_group_mix',
                same_space_group_mix: 'same_space_group_mix',
                client_space_choice: 'client_space_choice',
                min_players_to_play: 'min_places_to_play',
            }
            return mapping[storeFieldName] || storeFieldName
        },
        handleMultiSpaceGroupMixChange(value) {
            // Clear validation error when user interacts with switches
            if (this.errors.advancedOptions) this.$delete(this.errors, 'advancedOptions')

            if (value) {
                // Deactivate client_space_choice (mutually exclusive)
                // Dispatch null (not 0) — this is a CASCADE deactivation, not a user choice.
                // null = "undecided" → matching skips it. 0 = "user chose OFF" → matching filters on it.
                if (this.client_space_choice) {
                    console.log('🔄 Step3 - Deactivating client_space_choice (cascade from multi_space_group_mix)')
                    this.client_space_choice = false
                    this.$store.dispatch('experience/setUserSelection', {
                        key: 'client_space_choice',
                        value: null,
                    })
                }

                // Deactivate prohibit_mixing (no sense when mixing is globally allowed)
                if (this.prohibit_mixing_based_on_the_number_of_people) {
                    console.log('🔄 Step3 - Deactivating prohibit_mixing (cascade from multi_space_group_mix)')
                    this.prohibit_mixing_based_on_the_number_of_people = false
                    this.$store.dispatch('experience/setUserSelection', {
                        key: 'mixing_limit',
                        value: null,
                    })
                }
            }

            // OFF = null (undecided, skip in matching), ON = 1 (explicit choice)
            // Switch default is OFF → can't distinguish "never touched" from "toggled off"
            this.$store.dispatch('experience/setUserSelection', {
                key: 'multi_space_group_mix',
                value: value ? 1 : null,
            })
            console.log('🔵 Step3 - multi_space_group_mix changed:', value, '→ store:', value ? 1 : null)

            // Store the last changed field for reset logic
            if (value) {
                this.lastChangedField = 'multi_space_group_mix'
            }
        },
        handleSameSpaceGroupMixChange(value) {
            if (this.errors.advancedOptions) this.$delete(this.errors, 'advancedOptions')

            // OFF = null (undecided, skip in matching), ON = 1 (explicit choice)
            this.$store.dispatch('experience/setUserSelection', {
                key: 'same_space_group_mix',
                value: value ? 1 : null,
            })
            console.log('🔵 Step3 - same_space_group_mix changed:', value, '→ store:', value ? 1 : null)

            // Store the last changed field for reset logic
            if (value) {
                this.lastChangedField = 'same_space_group_mix'
            }
        },
        handleClientSpaceChoiceChange(value) {
            if (this.errors.advancedOptions) this.$delete(this.errors, 'advancedOptions')

            // Mutual exclusivity: If activating client_space_choice, deactivate multi_space_group_mix
            // Dispatch null (not 0) — cascade deactivation, not user choice
            if (value && this.multi_space_group_mix) {
                console.log('🔄 Step3 - Deactivating multi_space_group_mix (cascade from client_space_choice)')
                this.multi_space_group_mix = false
                this.$store.dispatch('experience/setUserSelection', {
                    key: 'multi_space_group_mix',
                    value: null,
                })
            }

            // OFF = null (undecided, skip in matching), ON = 1 (explicit choice)
            this.$store.dispatch('experience/setUserSelection', {
                key: 'client_space_choice',
                value: value ? 1 : null,
            })
            console.log('🔵 Step3 - client_space_choice changed:', value, '→ store:', value ? 1 : null)

            // Store the last changed field for reset logic
            if (value) {
                this.lastChangedField = 'client_space_choice'
            }
        },

        /**
         * « Durée dynamique » : cocher DÉCLARE un bowling.
         *
         * C'est la seule clé de détection qui définit un circuit à elle seule. Elle applique donc
         * aussi le préréglage bowling — sans quoi la détection ne trouverait rien et aucune
         * sous-catégorie ne serait écrite, en silence.
         *
         * Décocher ne défait PAS le préréglage : l'utilisateur reprend la main sur des réglages
         * qu'il voit à l'écran, on ne les modifie pas dans son dos.
         */
        handleDynamicDurationChange(value) {
            this.$store.dispatch('experience/setUserSelection', {
                key: 'dynamic_duration_enabled',
                value: value ? 1 : null,
            })

            if (!value) return

            // Préréglage bowling : pas de limite de mixage, et prix PAR GROUPE (décision 2026-09-15 :
            // un bowling est toujours tarifé par groupe, l'étape Tarification verrouille ce choix et
            // le serveur l'impose). Ce mode n'influence pas la détection faisant autorité :
            // l'empreinte bowling ne porte pas les clés tarifaires.
            //
            // On ne touche VOLONTAIREMENT PAS à « Afficher le nom des installations au client »
            // (`client_space_choice`) : c'est un choix commercial qui appartient au gérant, et un
            // bowling est parfaitement légitime avec ou sans. Depuis que la case « durée dynamique »
            // discrimine le circuit à elle seule, cette clé n'a plus besoin d'être forcée — et le
            // switch éteint envoie `null` (un joker), donc la détection tombe sur bowling dans les
            // trois états possibles : jamais touché, activé, ou désactivé.
            this.multi_space_group_mix = false
            this.prohibit_mixing_based_on_the_number_of_people = false

            const preset = {
                multi_space_group_mix: null,
                mixing_limit: 0,
                price_per_person: 0,
                price_per_group: 1,
            }

            Object.entries(preset).forEach(([key, presetValue]) => {
                this.$store.dispatch('experience/setUserSelection', { key, value: presetValue })
            })

            this.lastChangedField = 'dynamic_duration_enabled'
        },
        autoDeactivateIncompatibleFields(circuits) {
            if (!circuits || circuits.length === 0) return

            // List of fields to check
            const fieldsToCheck = [
                { store: 'multi_space_group_mix', local: 'multi_space_group_mix' },
                { store: 'same_space_group_mix', local: 'same_space_group_mix' },
                { store: 'client_space_choice', local: 'client_space_choice' },
                { store: 'min_players_to_play', local: 'min_places_to_play' },
                { store: 'mixing_limit', local: 'prohibit_mixing_based_on_the_number_of_people' },
            ]

            const storeSelections = this.$store.getters['experience/userSelections']

            fieldsToCheck.forEach((field) => {
                // If the store value is null, user hasn't made a choice yet — don't touch it
                // This prevents default component values (e.g. min_places_to_play=1) from polluting detection
                const storeValue = storeSelections[field.store]
                if (storeValue === null || storeValue === undefined) return

                // Only auto-deactivate fields that are currently ACTIVE
                const localValue = this[field.local]
                const isActive = localValue === true || localValue === 1 || (typeof localValue === 'number' && localValue > 0)

                if (!isActive) return // Skip — field is already off, don't touch the store

                // Collect all values for this field across matching circuits
                const values = new Set()
                circuits.forEach((circuitType) => {
                    const config = this.$store.getters['experience/getPrestationTypeConfig'](circuitType)
                    if (config) {
                        values.add(config[field.store])
                    }
                })

                // If all circuits agree on 0, auto-deactivate
                // Dispatch null (not 0) — this is an AUTOMATIC deactivation, not a user choice.
                // null = "undecided" → matching skips it. 0 = "user chose OFF" → matching filters on it.
                if (values.has(0) && values.size === 1) {
                    console.log(`🔄 Auto-deactivating ${field.store} (all circuits want 0)`)
                    const deactivationValue = typeof localValue === 'number' ? 0 : false
                    this[field.local] = deactivationValue
                    this.$store.dispatch('experience/setUserSelection', {
                        key: field.store,
                        value: null,
                    })
                }
                // If circuits disagree, auto-deactivate too
                else if (values.size > 1) {
                    console.log(`🔄 Auto-deactivating ${field.store} (circuits disagree: ${Array.from(values).join(' vs ')})`)
                    const deactivationValue = typeof localValue === 'number' ? 0 : false
                    this[field.local] = deactivationValue
                    this.$store.dispatch('experience/setUserSelection', {
                        key: field.store,
                        value: null,
                    })
                }
                // If all circuits agree on 1, field stays active (do nothing)
            })
        },
        resetAllFields() {
            console.log('🔄 Resetting all fields except:', this.lastChangedField)

            // Reset all fields except the last changed one
            // Dispatch null (not 0) — reset = "undecided", not "user chose OFF"
            if (this.lastChangedField !== 'multi_space_group_mix') {
                this.multi_space_group_mix = false
                this.$store.dispatch('experience/setUserSelection', {
                    key: 'multi_space_group_mix',
                    value: null,
                })
            }

            if (this.lastChangedField !== 'same_space_group_mix') {
                this.same_space_group_mix = false
                this.$store.dispatch('experience/setUserSelection', {
                    key: 'same_space_group_mix',
                    value: null,
                })
            }

            if (this.lastChangedField !== 'client_space_choice') {
                this.client_space_choice = false
                this.$store.dispatch('experience/setUserSelection', {
                    key: 'client_space_choice',
                    value: null,
                })
            }
        },
        async clearExperiencePlanning() {
            const id = this.localPrestation?.id || this.prestation?.id
            if (!id) return
            try {
                await axios.delete(`/api/v3/experience/planning/${id}`)
                console.log('🗑️ Step3 - Planning data cleared for prestation:', id)
            } catch (e) {
                console.error('❌ Step3 - Failed to clear planning:', e.response?.data?.error || e.message)
            }
        },
    },
    watch: {
        'form.karafun'(newValue) {
            if (!newValue) this.form.devices = []
        },
        selectedOptionReservationMode: {
            handler(newVal, oldVal) {
                if (!newVal) return

                this.$store.dispatch('experience/setReservationMode', newVal)
                this.$store.dispatch('experience/setUserSelection', {
                    key: 'reservation_mode',
                    value: newVal,
                })

                // If one_schedule_for_everyone or free_access_all_day, only 1 installation can be selected
                // Keep only the first selected installation, deselect others
                if (newVal === 'one_schedule_for_everyone' || newVal === 'free_access_all_day') {
                    const installations = [...this.$store.getters['experience/installations']]
                    const selectedInstallations = installations.filter((i) => i.selected && !i.deleted)

                    if (selectedInstallations.length > 1) {
                        let firstFound = false
                        installations.forEach((inst) => {
                            if (inst.selected && !inst.deleted) {
                                if (!firstFound) {
                                    firstFound = true // Keep the first one
                                } else {
                                    inst.selected = false // Deselect others
                                }
                            }
                        })
                        this.$store.dispatch('experience/setInstallations', installations)

                        if (this.warningTimer) clearTimeout(this.warningTimer)

                        this.$set(
                            this.errors,
                            'oneScheduleInstallation',
                            this.$t('experience.step3.oneScheduleOnlyOneInstallation')
                        )
                        this.warningTimer = setTimeout(() => {
                            this.$set(this.errors, 'oneScheduleInstallation', null)
                            this.warningTimer = null
                        }, 9000)
                    } else {
                        this.clearOneScheduleError()
                    }
                }

                if (oldVal && newVal !== oldVal) {
                    console.log('🔄 Step3 - Reservation mode changed, resetting other selections')
                    // Reset all Step3/Step5 selections
                    this.$store.dispatch('experience/setUserSelection', { key: 'price_per_person', value: null })
                    this.$store.dispatch('experience/setUserSelection', { key: 'price_per_group', value: null })
                    this.$store.dispatch('experience/setUserSelection', { key: 'multi_space_group_mix', value: null })
                    this.$store.dispatch('experience/setUserSelection', { key: 'same_space_group_mix', value: null })
                    this.$store.dispatch('experience/setUserSelection', { key: 'client_space_choice', value: null })
                    this.$store.dispatch('experience/setUserSelection', { key: 'min_players_to_play', value: null })
                    this.$store.dispatch('experience/setUserSelection', { key: 'mixing_limit', value: null })
                    // ✅ Reset local UI switches to defaults (Options avancées)
                    this.multi_space_group_mix = false
                    this.same_space_group_mix = false
                    this.client_space_choice = false
                    this.prohibit_mixing_based_on_the_number_of_people = false
                    this.the_number_of_people = 1
                    this.$store.dispatch('experience/setAuthoriseMixed', false)
                    // ✅ Clear planning data in DB when mode changes
                    this.clearExperiencePlanning()
                    console.log('🔄 Step3 - Reset all local switches to defaults')
                }
            },
            immediate: true,
        },
        prestation: {
            async handler(newVal) {
                if (newVal) {
                    this.localPrestation = newVal

                    // Populate from user_choices
                    const prohibitMixing = this.getUserChoiceValue('prohibit_mixing_min_people')
                    if (prohibitMixing !== null) {
                        this.prohibit_mixing_based_on_the_number_of_people =
                            prohibitMixing == 1 || prohibitMixing == '1' || prohibitMixing == true || prohibitMixing == 'true'
                        // Only dispatch to store if ON (1). OFF from DB = null (undecided)
                        if (this.prohibit_mixing_based_on_the_number_of_people) {
                            this.$store.dispatch('experience/setUserSelection', {
                                key: 'mixing_limit',
                                value: 1,
                            })
                        }
                    }

                    const prohibitMixingValue = this.getUserChoiceValue('prohibited_mixing_min_people_value')
                    if (prohibitMixingValue !== null) {
                        this.the_number_of_people = parseInt(prohibitMixingValue)
                        // Sync min_ticket immediately (don't rely on queued watcher)
                        if (this.prohibit_mixing_based_on_the_number_of_people && parseInt(prohibitMixingValue) > 0) {
                            this.$store.dispatch('experience/setMinTicket', parseInt(prohibitMixingValue))
                        }
                    }

                    // ✅ FIX: Load multi_space_group_mix from user_choices
                    const multiSpaceGroupMix = this.getUserChoiceValue('multi_space_group_mix')
                    if (multiSpaceGroupMix !== null) {
                        this.multi_space_group_mix =
                            multiSpaceGroupMix == 1 ||
                            multiSpaceGroupMix == '1' ||
                            multiSpaceGroupMix == true ||
                            multiSpaceGroupMix == 'true'
                        // Only dispatch to store if ON (1). OFF from DB = null (undecided)
                        // DB doesn't distinguish "never set" from "explicitly OFF"
                        if (this.multi_space_group_mix) {
                            this.$store.dispatch('experience/setUserSelection', {
                                key: 'multi_space_group_mix',
                                value: 1,
                            })
                        }
                    }

                    const clientSpaceChoice = this.getUserChoiceValue('client_space_choice')
                    if (clientSpaceChoice !== null) {
                        this.client_space_choice =
                            clientSpaceChoice == 1 ||
                            clientSpaceChoice == '1' ||
                            clientSpaceChoice == true ||
                            clientSpaceChoice == 'true'
                        // Only dispatch to store if ON (1). OFF from DB = null (undecided)
                        if (this.client_space_choice) {
                            this.$store.dispatch('experience/setUserSelection', {
                                key: 'client_space_choice',
                                value: 1,
                            })
                        }
                    }

                    // Durée dynamique : la valeur revient en CHAÎNE depuis prestation_user_choices.
                    // On dispatche 1 ou null — jamais 0 — pour rester sur la convention locale ;
                    // NULL_MEANS_ZERO fait le reste côté détection.
                    const dynamicDuration = this.getUserChoiceValue('dynamic_duration_enabled')
                    if (dynamicDuration !== null) {
                        this.dynamic_duration_enabled =
                            dynamicDuration == 1 || dynamicDuration == '1' || dynamicDuration == true || dynamicDuration == 'true'

                        this.$store.dispatch('experience/setUserSelection', {
                            key: 'dynamic_duration_enabled',
                            value: this.dynamic_duration_enabled ? 1 : null,
                        })
                    }

                    // Le switch reflète prestations.authorise_mixed, ce que le moteur de réservation
                    // applique réellement (l'étape 3 écrit la colonne au save). Repli sur le choix
                    // enregistré si la colonne est absente. SimpleTicket garde le choix : son circuit
                    // exige 0, et un ON venu du défaut de colonne casserait la détection.
                    const isSimpleTicket = this.getUserChoiceValue('detected_type') === 'simpleTicket'
                    const authoriseMixed = newVal.authorise_mixed
                    const sameSpaceGroupMix =
                        !isSimpleTicket && authoriseMixed !== undefined && authoriseMixed !== null
                            ? authoriseMixed
                            : this.getUserChoiceValue('same_space_group_mix')
                    if (sameSpaceGroupMix !== null) {
                        // ✅ FIX: Use correct variable name (underscore, not camelCase)
                        this.same_space_group_mix =
                            sameSpaceGroupMix == 1 ||
                            sameSpaceGroupMix == '1' ||
                            sameSpaceGroupMix == true ||
                            sameSpaceGroupMix == 'true'
                        // Only dispatch to store if ON (1). OFF from DB = null (undecided)
                        if (this.same_space_group_mix) {
                            this.$store.dispatch('experience/setUserSelection', {
                                key: 'same_space_group_mix',
                                value: 1,
                            })
                        }
                    }

                    const minPlaces = this.getUserChoiceValue('min_places_to_play')
                    if (minPlaces !== null) {
                        this.min_places_to_play = parseInt(minPlaces)
                        // Only dispatch to store if ON (>0). OFF from DB = null (undecided)
                        if (this.min_places_to_play > 0) {
                            this.$store.dispatch('experience/setUserSelection', {
                                key: 'min_players_to_play',
                                value: 1,
                            })
                        }
                    }

                    const reservationMode = this.getUserChoiceValue('reservation_mode')
                    if (reservationMode) {
                        this.selectedOptionReservationMode = reservationMode
                        this.$store.dispatch('experience/setReservationMode', reservationMode)
                        this.$store.dispatch('experience/setUserSelection', {
                            key: 'reservation_mode',
                            value: reservationMode,
                        })
                    }

                    const pricingMode = this.getUserChoiceValue('pricing_mode')
                    if (pricingMode) {
                        this.pricing_mode = pricingMode
                        // ✅ FIX: Do NOT apply business rules during initial data loading
                        // Business rules should only apply when user actively changes values
                        // Otherwise, we override the user's saved choices from the database
                    }
                }

                await this.fetchMateriels()

                if (newVal) {
                    this.$nextTick(() => {
                        this.isInitialLoad = false
                        this.initialDataString = this.getFormDataString()
                    })
                }
            },
            immediate: true,
            deep: true,
        },
        spaces_id: {
            async handler(newVal) {
                if (Array.isArray(newVal)) {
                    this.localSpaces = newVal

                    // Skip if store already has installations (navigating back — preserve user selections)
                    const existingInstallations = this.$store.getters['experience/installations']
                    console.log('existingInstallations', existingInstallations)
                    if (existingInstallations && existingInstallations.length > 0) return

                    // Skip initial fetch only if store is already populated (avoids double call/flash)
                    // if (newVal.length === 0 && this.id) return

                    await this.fetchSpaces(1)
                    this.$nextTick(() => {
                        this.initialDataString = this.getFormDataString()
                    })
                }
            },
            immediate: true,
            deep: true,
        },
        installations: {
            handler() {
                if (this.countChecked() > 0 && this.errors.installations) {
                    this.$delete(this.errors, 'installations')
                }
            },
            deep: true,
        },
        the_number_of_people: {
            handler(newValue) {
                // Si vide string, mettre 0 et supprimer l'erreur
                console.log(newValue)
                if (newValue === '') {
                    this.$set(this.errors, 'the_number_of_people', this.$t('experience.errors.required'))
                    return
                }

                // Validation classique
                if (newValue <= 0) {
                    this.$set(this.errors, 'the_number_of_people', this.$t('experience.errors.required'))
                } else {
                    this.$delete(this.errors, 'the_number_of_people')
                    // Sync with min_ticket in Step 5
                    this.$store.dispatch('experience/setMinTicket', Number(newValue))
                }
            },
            deep: true,
        },
        prohibit_mixing_based_on_the_number_of_people: {
            handler(newValue) {
                if (this.errors.advancedOptions) this.$delete(this.errors, 'advancedOptions')

                if (!newValue) {
                    this.the_number_of_people = 1
                    this.$delete(this.errors, 'the_number_of_people')
                }
                // Sync mixing_limit to store for circuit detection
                this.$store.dispatch('experience/setUserSelection', {
                    key: 'mixing_limit',
                    value: newValue ? 1 : null,
                })
            },
            deep: true,
        },
        reservationMode: {
            handler(newMode, oldMode) {
                // When mode is 'one_schedule_for_everyone', deselect all installations
                // User must make a fresh selection
                // ✅ FIX: Only deselect when user actively changes the mode, not on initial load/re-mount
                if (oldMode !== undefined && (newMode === 'one_schedule_for_everyone' || newMode === 'free_access_all_day')) {
                    const installations = [...this.installations]
                    installations.forEach((inst) => {
                        if (!inst.deleted) {
                            inst.selected = false
                        }
                    })
                    this.$store.dispatch('experience/setInstallations', installations)
                }
            },
            immediate: true,
        },
        currentCircuit: {
            handler(newCircuit, oldCircuit) {
                if (newCircuit && newCircuit !== oldCircuit) {
                    console.log(`🎯 Step3 - Circuit detected: ${newCircuit}`)
                    console.log('ℹ️  You can continue to modify fields freely')
                } else if (!newCircuit && oldCircuit) {
                    console.log('🔓 Step3 - No circuit matches current selections')
                }
            },
            immediate: false,
        },
        matchingCircuits: {
            handler(circuits) {
                // ✅ FIX: Smart auto-deactivation
                // - During initial load: Preserve all saved values (don't auto-deactivate)
                // - After user interaction: Auto-deactivate incompatible fields intelligently

                if (this.isInitialLoad) {
                    console.log('⏳ Initial load - skipping auto-deactivation (preserving DB values)')
                    return
                }

                if (circuits.length > 1) {
                    console.log(`🔍 Step3 - Multiple circuits match: ${circuits.join(', ')}`)

                    // Log which fields are disabled (UI only)
                    const disabledFields = []
                    if (this.isMultiSpaceGroupMixDisabled) disabledFields.push('multi_space_group_mix')
                    if (this.isSameSpaceGroupMixDisabled) disabledFields.push('same_space_group_mix')
                    if (this.isClientSpaceChoiceDisabled) disabledFields.push('client_space_choice')
                    if (this.isMinPlayersToPlayDisabled) disabledFields.push('min_players_to_play')
                    if (this.isMixingLimitDisabled) disabledFields.push('mixing_limit')

                    if (disabledFields.length > 0) {
                        console.log(`🔒 Step3 - Fields disabled in UI: ${disabledFields.join(', ')}`)
                    }

                    // ✅ Auto-deactivate incompatible fields (only after initial load)
                    this.autoDeactivateIncompatibleFields(circuits)
                } else if (circuits.length === 1) {
                    console.log(`🎯 Step3 - Single circuit match: ${circuits[0]}`)
                    // ✅ Auto-deactivate incompatible fields (only after initial load)
                    this.autoDeactivateIncompatibleFields(circuits)
                } else if (circuits.length === 0) {
                    console.log('❌ Step3 - No circuits match current selections')
                    console.log('ℹ️  This is OK - user can explore freely and adjust fields')

                    // DISABLED: Auto-reset causes infinite loops
                    // Users should be free to explore field combinations
                    // If they create an invalid config, they'll see it when saving

                    // const hasActiveFields = this.multi_space_group_mix ||
                    //                       this.same_space_group_mix ||
                    //                       this.client_space_choice
                    //
                    // if (hasActiveFields) {
                    //     console.log('🔄 Resetting all fields (invalid configuration)')
                    //     this.resetAllFields()
                    // }
                }
            },
            immediate: false,
        },
    },
    mounted() {
        this.checkKarafunIntegration()
        this.fetchKarafunDevices()

        // For new creation (no prestation prop), mark initial load as complete
        // so the matchingCircuits watcher can run autoDeactivateIncompatibleFields
        if (!this.prestation) {
            this.$nextTick(() => {
                this.isInitialLoad = false
                console.log('✅ New creation - isInitialLoad set to false')
            })
        }
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}
</style>
